import { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { Field, reduxForm, FieldArray } from 'redux-form';
import { Modal, Button, Icon, Segment, Form, Header, Image, Message, Grid } from "semantic-ui-react";
import { getCommonAPI, getDppAPI, renderTextInput, renderMultipleSelectInput, renderCheckbox, renderSelectInput, renderRadioInput, renderTextArea } from 'hpp-fe-common';
import { DateTime } from 'luxon';
import { partnerIDOperationInprogress, partnerIDSupportDocumentsProcessed, partnerIDOperationProcessed, partnerIDOperationCompleted } from './partnerIDSlice'
import { signOut } from "../auth/authSlice";
import InfoModal from "./info-modal/InfoModal";
import axios from "axios";


const PartnerID = ({ handleClose, handleSubmit }) => {
    const dispatch = useDispatch();

    const { email, _id, country } = JSON.parse(window.localStorage.getItem('user'));

    const { loading, submitted, error, errorMessage, errorMessageData } = useSelector(state => state.partnerID);

    // State for handling image uploads
    const [selectedProfileImage, setSelectedProfileImage] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(null);

    const isAgreement1TrueFromState = useSelector(state => state?.form?.partnerID?.values?.agreement1)
    const isAgreement2TrueFromState = useSelector(state => state?.form?.partnerID?.values?.agreement2)
    const anyValidationError = useSelector(state => state?.form?.partnerID?.syncErrors)
    const haveExperienceInCCTVFromState = useSelector(state => state?.form?.partnerID?.values?.haveExperienceInCCTV);
    const haveExperienceInNonCCTVFromState = useSelector(state => state?.form?.partnerID?.values?.haveExperienceInNonCCTV);

    // Define the minimum and maximum years
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 30; // Example: 30 years back
    const maxYear = currentYear; // Current year

    // Create an array of year options
    const yearOptions = Array.from({ length: maxYear - minYear + 1 }, (_, index) => {
        const year = minYear + index;
        return { key: year, text: year, value: year };
    });

    const hikCertifications = [
        { key: "hcsa", text: 'HCSA', value: "hcsa" },
        { key: "cgsa", text: 'CGSA', value: "cgsa" },
        { key: "hcsp", text: 'HCSP', value: "hcsp" },
        { key: "cgsp", text: 'CGSP', value: "cgsp" }
    ]

    const hikvisionProucts = [
        { key: "accessControl", text: 'Access Control', value: "accessControl" },
        { key: "cctv", text: 'CCTV', value: "cctv" },
        { key: "videoIntercom", text: 'Video Intercom', value: "videoIntercom" },
        { key: "alarm", text: 'Alarm', value: "alarm" },
        { key: "ifpd", text: 'IFPD', value: "ifpd" },
        { key: "networkSwitch", text: 'Network Switch', value: "networkSwitch" },
        { key: "other", text: 'Other', value: "other" }
    ]

    const companyRoles = [
        { key: "owner", text: 'Owner', value: "owner" },
        { key: "technician", text: 'Technician', value: "technician" },
        { key: "salesPerson", text: 'Salesperson', value: "salesPerson" },
        { key: "projectManager", text: 'Project Manager', value: "projectManager" },
        { key: "customerServiceRepresentative", text: 'Customer Service Representative', value: "customerServiceRepresentative" },
        { key: "accountManager", text: 'Account Manager', value: "accountManager" },
        { key: "itSpecialist", text: 'IT Specialist', value: "itSpecialist" }
    ]

    const provincesOfSriLanka = [
        { key: "central", text: "Central Province", value: "central" },
        { key: "eastern", text: "Eastern Province", value: "eastern" },
        { key: "northern", text: "Northern Province", value: "northern" },
        { key: "southern", text: "Southern Province", value: "southern" },
        { key: "western", text: "Western Province", value: "western" },
        { key: "northCentral", text: "North Central Province", value: "northCentral" },
        { key: "northWestern", text: "North Western Province", value: "northWestern" },
        { key: "uva", text: "Uva Province", value: "uva" },
        { key: "sabaragamuwa", text: "Sabaragamuwa Province", value: "sabaragamuwa" }
    ];


    useEffect(() => {
        const cancelToken = axios.CancelToken.source();

        const fetchSupportLinks = async () => {
            dispatch(partnerIDOperationInprogress());

            try {
                const { data } = await getDppAPI().get('/api_v1/partnerID/get-support-links', { cancelToken: cancelToken.token });

                // Check for authentication
                if (data.authenticated === false) {
                    return dispatch(signOut({
                        error: true,
                        errorMessage: data.errorMessage,
                        errorMessageData: data.errorMessageData
                    }));
                }

                // Dispatch processed documents
                dispatch(partnerIDSupportDocumentsProcessed(data));

            } catch (error) {
                dispatch(partnerIDSupportDocumentsProcessed({
                    error: true,
                    errorMessage: 'Error loading support documents data. Please contact the system administrator.',
                    errorMessageData: error.toString(),
                }));
            }
        };

        fetchSupportLinks();

        return () => {
            cancelToken.cancel();
        };

    }, [dispatch]);



    const handleModalClose = () => {
        dispatch(partnerIDOperationCompleted())
        handleClose();
    }


    const renderPlatformFields = ({ fields }) => {
        const platformsOptions = [
            { key: 'facebook', text: 'Facebook', value: 'facebook' },
            { key: 'youtube', text: 'YouTube', value: 'youtube' },
            { key: 'tiktok', text: 'TikTok', value: 'tiktok' },
        ];

        // Maximum number of platforms allowed
        const maxPlatforms = 3;

        // Gather currently selected platforms
        const selectedPlatforms = fields.map(field => {
            const platformName = field.name;
            return platformName ? platformName.split('.')[1] : null; // Extract platform name
        }).filter(Boolean);

        return (
            <div>
                {/* Map over each field to create the platform selection */}
                {fields.map((platform, index) => {
                    // Filter out selected options for the current dropdown
                    const availableOptions = platformsOptions.filter(option =>
                        !selectedPlatforms.includes(option.value) || selectedPlatforms[index] === option.value
                    );

                    return (
                        <Form.Group widths='equal' key={index}>
                            <Form.Field>
                                <Field
                                    name={`${platform}.name`}
                                    component={renderSelectInput}
                                    otherProps={{
                                        label: `Platform ${index + 1}`,
                                        options: availableOptions,
                                        placeholder: 'Select a platform',
                                        required: true,
                                        disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                    }}
                                />

                            </Form.Field>
                            <Form.Field>
                                <Field
                                    name={`${platform}.link`}
                                    component={renderTextInput}
                                    otherProps={{
                                        label: `Platform Link ${index + 1}`,
                                        placeholder: 'Enter platform link',
                                        required: true,
                                        disabled: isAgreement1TrueFromState
                                    }}
                                />

                            </Form.Field>
                            <Icon
                                name='remove' // Use an appropriate icon name
                                onClick={() => fields.remove(index)}
                                disabled={isAgreement1TrueFromState || isAgreement2TrueFromState}
                                title="Remove Platform" // Optional: tooltip for better accessibility
                            />
                        </Form.Group>
                    );
                })}
                {/* Button to add new platform fields, disabled if max platforms are reached */}
                {fields.length < maxPlatforms && (
                    <Button type="button" onClick={() => fields.push({})} disabled={isAgreement1TrueFromState || isAgreement2TrueFromState}>
                        Add Platform
                    </Button>
                )}
            </div>
        );
    };


    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setSelectedProfileImage(file);

        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setProfileImagePreview(null);
        }
    };

    const removeFilePreview = () => {
        setSelectedProfileImage(null);
        setProfileImagePreview(null);

        // Reset the file input value to allow the same file to be selected again
        const fileInput = document.getElementById('file-input');
        if (fileInput) {
            fileInput.value = ''; // Reset the input value
        }
    };

    const getFileType = (fileName) => {
        // Split the file name by comma and trim any extra whitespace
        const files = fileName.split(',').map(file => file.trim());

        // Extract the file extension from each file name
        const fileTypes = files.map(file => {
            const parts = file.split('.');
            return parts.length > 1 ? parts.pop() : 'unknown';
        });

        return fileTypes;
    };

    const handlePartnerIDData = async (formValues) => {
        const {
            birthday,
            hikID,
            nic,
            city,
            province,
            fullAddress,
            haveExperienceInNonCCTV,
            haveExperienceInCCTV,
            cctvStartYear,
            nonCCTVStartYear,
            hikvisionCertification,
            roleInCompany,
            numberOfSitesCompletedInCompany,
            usedHikvisionProducts,
            hppCoBrandingStatus,
            socialMediaPlatforms
        } = formValues

        try {
            dispatch(partnerIDOperationInprogress());


            // File is selected, handle file upload
            const timestamp = DateTime.now().toFormat('yyyyMMddHHmmss');
            const customizedDocID = `${hikID}_${timestamp}`;

            const fileExtensionType = getFileType(selectedProfileImage?.name);
            const uploadFormData = new FormData();
            uploadFormData.append('file', selectedProfileImage);

            const uploadResponse = await getCommonAPI().post(
                `/api_v1/files/upload/single-file?portal=inst&fileType=partner-id-profile-image&fileDocID=${customizedDocID}&fileExtension=${fileExtensionType}`,
                uploadFormData,
                {
                    timeout: 120000,
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (uploadResponse.data.authenticated === false) {
                return dispatch(signOut({
                    error: true,
                    errorMessage: uploadResponse.data.errorMessage,
                    errorMessageData: uploadResponse.data.errorMessageData,
                }));
            }

            if (uploadResponse.data?.error) {
                return dispatch(partnerIDOperationProcessed({
                    error: true,
                    errorMessage: uploadResponse.data.errorMessage,
                    errorMessageData: uploadResponse.data.errorMessageData,
                }));
            }

            if (!uploadResponse.data?.key) {
                return dispatch(partnerIDOperationProcessed({
                    error: true,
                    errorMessage: "Bad response from server. File upload not successful",
                    errorMessageData: 'Please contact the system administrator',
                }));
            }

            const userDetails = {
                user: _id,
                partnerIDImageFileKey: uploadResponse.data.key,
                birthday,
                hikID,
                nic,
                city,
                province,
                fullAddress,
                haveExperienceInNonCCTV,
                haveExperienceInCCTV,
                hikvisionCertification,
                cctvStartYear,
                nonCCTVStartYear,
                roleInCompany,
                numberOfSitesCompletedInCompany,
                usedHikvisionProducts,
                hppCoBrandingStatus
            }

            if (haveExperienceInCCTVFromState === 'yes') {
                userDetails.cctvStartYear = cctvStartYear
            } else {
                userDetails.cctvStartYear = 0
            }

            if (haveExperienceInNonCCTVFromState === 'yes') {
                userDetails.nonCCTVStartYear = nonCCTVStartYear
            } else {
                userDetails.nonCCTVStartYear = 0
            }
            // Check if socialMediaPlatforms exist and add them
            if (socialMediaPlatforms && socialMediaPlatforms.length > 0) {
                userDetails.socialMediaPlatforms = socialMediaPlatforms; // Add the socialMediaPlatforms field
            }

            // Submit chat with file
            const chatResponse = await getDppAPI().post(`/api_v1/partnerID/add-one`, userDetails);

            if (chatResponse.data.authenticated === false) {
                return dispatch(signOut({
                    error: true,
                    errorMessage: chatResponse.data.errorMessage,
                    errorMessageData: chatResponse.data.errorMessageData,
                }));
            }

            dispatch(partnerIDOperationProcessed(chatResponse.data));


        } catch (error) {
            dispatch(partnerIDOperationProcessed({
                error: true,
                errorMessage: "Unidentified error. Please contact the system administrator",
                errorMessageData: error.toString(),
            }));
        }
    };

    if (error) return (
        <>
            <Modal.Content>
                <Message negative>
                    <Message.Header>{errorMessage}</Message.Header>
                    <p>{errorMessageData}</p>
                </Message>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={handleModalClose}>
                    <Icon name='remove' /> Close
                </Button>

            </Modal.Actions>
        </>
    )

    if (submitted) return (
        <>
            <Modal.Content>
                <Message success>
                    <Message.Header>Your Partner ID request has been submitted successfully.</Message.Header>
                    <p>
                        Our team will review and approve your request within 7–10 business days.
                        You may log out now and check the status later by logging back in.
                    </p>
                </Message>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={handleModalClose}>
                    <Icon name='remove' /> Close
                </Button>

            </Modal.Actions>
        </>
    )



    return (
        <>
            <Modal.Content>
                <Segment basic loading={loading} style={{ height: '700px', overflowY: 'auto' }}>
                    <Form autoComplete='off' >

                        {/* Personal Information */}
                        <Segment>
                            <Header as='h3'>Personal Information</Header>
                            <Grid stackable>
                                <Grid.Row columns={2}>
                                    <Grid.Column width={4} textAlign="center">
                                        <Form.Field>
                                            <label>Profile Picture</label>
                                            <Segment
                                                style={{
                                                    border: '2px dashed #007bff',
                                                    borderRadius: '8px',
                                                    padding: '20px',
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedProfileImage ? '#f9f9f9' : '#fff',
                                                    transition: 'background-color 0.2s ease',
                                                }}
                                                onClick={() => document.getElementById('file-input').click()} // Open file dialog on segment click
                                            >
                                                <input
                                                    type="file"
                                                    id="file-input"
                                                    accept=".jpg, .png, .jpeg"
                                                    onChange={handleFileChange}
                                                    style={{ display: 'none' }} // Hide the default file input
                                                />
                                                {profileImagePreview ? (
                                                    <Segment raised>
                                                        <Image src={profileImagePreview} size='small' centered />
                                                        <Button
                                                            icon='close'
                                                            color='red'
                                                            size='mini'
                                                            onClick={removeFilePreview}
                                                            title='Remove selected file'
                                                            style={{ marginTop: '5px' }}
                                                        />
                                                    </Segment>
                                                ) : selectedProfileImage ? (
                                                    <Message
                                                        onDismiss={removeFilePreview}
                                                        content={selectedProfileImage.name}
                                                        color="green"
                                                        style={{ textAlign: 'left' }}
                                                    />
                                                ) : (
                                                    <>
                                                        <Header icon size='small'>
                                                            <Icon name='upload' />
                                                            Select or Drag & Drop your file here
                                                        </Header>
                                                        <p style={{ marginTop: '10px', color: '#6c757d' }}>
                                                            Allowed file types: .jpg, .jpeg, .png
                                                        </p>
                                                    </>
                                                )}
                                            </Segment>
                                        </Form.Field>

                                        {/* Profile Image Guidelines Modal */}
                                        <InfoModal type="imageGuidelines" />


                                    </Grid.Column>

                                    <Grid.Column width={12}>
                                        <Form.Group widths='equal'>
                                            <Form.Field>
                                                <Field
                                                    name='hikID'
                                                    component={renderTextInput}
                                                    otherProps={{
                                                        label: 'HIK ID (Hik Partner Pro)',
                                                        placeholder: 'Enter your HIK ID',
                                                        required: true,
                                                        disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                    }}
                                                />
                                            </Form.Field>
                                            <Form.Field>
                                                <Field
                                                    name='nic'
                                                    component={renderTextInput}
                                                    otherProps={{
                                                        label: 'NIC',
                                                        placeholder: 'Enter your NIC',
                                                        required: true,
                                                        disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                    }}
                                                />
                                            </Form.Field>
                                        </Form.Group>
                                        <Form.Group widths="equal">
                                            <Form.Field >
                                                <Field
                                                    name='city'
                                                    component={renderTextInput}
                                                    otherProps={{
                                                        label: 'City',
                                                        placeholder: 'Enter your city',
                                                        required: true,
                                                        disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                    }}
                                                />
                                            </Form.Field>
                                            {/* <Form.Field >
                                                <Field
                                                    name='province'
                                                    component={renderTextInput}
                                                    otherProps={{
                                                        label: 'Province',
                                                        placeholder: 'Enter your Province',
                                                        required: true,
                                                        disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                    }}
                                                />
                                            </Form.Field> */}

                                            <Form.Field>
                                                {country?.key === 'lk' ? (
                                                    <Field
                                                        name='province'
                                                        component={renderSelectInput}
                                                        otherProps={{
                                                            label: 'Province',
                                                            placeholder: 'Select your Province',
                                                            options: provincesOfSriLanka,
                                                            required: true,
                                                            disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                        }}
                                                    />
                                                ) : (
                                                    <Field
                                                        name='province'
                                                        component={renderTextInput}
                                                        otherProps={{
                                                            label: 'Province',
                                                            placeholder: 'Enter your Province',
                                                            required: true,
                                                            disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                        }}
                                                    />
                                                )}
                                            </Form.Field>

                                        </Form.Group>

                                        <Form.Group widths="equal">
                                            <Form.Field>
                                                <Field
                                                    name='fullAddress'
                                                    component={renderTextArea}
                                                    otherProps={{
                                                        label: 'Full Address',
                                                        required: true,
                                                        placeholder: 'Enter address where the Partner ID should be sent.',
                                                        rows: 4, // Adjust initial display height
                                                        disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                    }}
                                                />
                                            </Form.Field>
                                        </Form.Group>

                                        <Form.Group widths="equal">
                                            <Form.Field >
                                                <Field
                                                    name='birthday'
                                                    component={renderTextInput}
                                                    otherProps={{
                                                        type: "date",
                                                        label: 'Date of Birth',
                                                        placeholder: 'Enter your birthday',
                                                        required: true,
                                                        disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                    }}
                                                />
                                            </Form.Field>
                                            <Form.Field >
                                                <Field
                                                    name='hikvisionCertification'
                                                    component={renderMultipleSelectInput}
                                                    otherProps={{
                                                        multiple: true,
                                                        fluid: true,
                                                        label: 'Hikvision Certification',
                                                        placeholder: 'Select Hikvision Certifications You Have',
                                                        options: hikCertifications,
                                                        disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                    }}
                                                />
                                            </Form.Field>

                                        </Form.Group>

                                        <Form.Group widths="equal">
                                            <Form.Field>
                                                <Field
                                                    name='haveExperienceInCCTV'
                                                    component={renderRadioInput}
                                                    otherProps={{
                                                        mainLabel: 'Do You have Experience In CCTV',
                                                        buttons: [
                                                            { label: 'Yes', value: 'yes' },
                                                            { label: 'No', value: 'no' }
                                                        ],
                                                        disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                    }}
                                                />
                                            </Form.Field>
                                            <Form.Field>
                                                <Field
                                                    name='haveExperienceInNonCCTV'
                                                    component={renderRadioInput}
                                                    otherProps={{
                                                        mainLabel: 'Do You have Experience In Non CCTV',
                                                        buttons: [
                                                            { label: 'Yes', value: 'yes' },
                                                            { label: 'No', value: 'no' }
                                                        ],
                                                        disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                    }}
                                                />
                                            </Form.Field>
                                        </Form.Group>

                                        <Form.Group widths="equal">
                                            <Form.Field>
                                                {haveExperienceInCCTVFromState === 'yes' && (
                                                    <Field
                                                        name='cctvStartYear'
                                                        component={renderSelectInput}
                                                        otherProps={{
                                                            label: 'Which year did you start CCTV?',
                                                            options: yearOptions,
                                                            required: true,
                                                            placeholder: 'Select a year',
                                                            disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                        }}
                                                    />
                                                )}
                                            </Form.Field>

                                            <Form.Field>
                                                {haveExperienceInNonCCTVFromState === 'yes' && (
                                                    <Field
                                                        name='nonCCTVStartYear'
                                                        component={renderSelectInput}
                                                        otherProps={{
                                                            label: 'Which year did you start Non-CCTV?',
                                                            options: yearOptions,
                                                            required: true,
                                                            placeholder: 'Select a year',
                                                            disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                                        }}
                                                    />
                                                )}
                                            </Form.Field>
                                        </Form.Group>
                                    </Grid.Column>
                                </Grid.Row>



                            </Grid>
                        </Segment>

                        {/* Company Information */}
                        <Segment>
                            <Header as='h3'>Company Information</Header>
                            <Form.Group widths='equal'>
                                <Form.Field>
                                    <Field
                                        name='roleInCompany'
                                        component={renderSelectInput}
                                        otherProps={{
                                            label: 'Role In Company',
                                            placeholder: 'Enter your Role In Company',
                                            options: companyRoles,
                                            required: true,
                                            disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                        }}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <Field
                                        name='numberOfSitesCompletedInCompany'
                                        component={renderTextInput}
                                        otherProps={{
                                            type: "number",
                                            min: 0,
                                            label: 'Number Of Sites Completed In Company',
                                            placeholder: 'Enter Number Of Sites Completed In Company',
                                            required: true,
                                            disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                        }}
                                    />
                                </Form.Field>
                            </Form.Group>

                            <Form.Group widths='equal'>
                                <Form.Field>
                                    <Field
                                        name='usedHikvisionProducts'
                                        component={renderMultipleSelectInput}
                                        otherProps={{
                                            multiple: true,
                                            fluid: true,
                                            label: 'Used Hikvision Products',
                                            placeholder: 'Select used Hikvision Products',
                                            options: hikvisionProucts,
                                            required: true,
                                            disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                        }}
                                    />
                                </Form.Field>

                                <Form.Field>
                                    <Field
                                        name='hppCoBrandingStatus'
                                        component={renderRadioInput}
                                        otherProps={{
                                            mainLabel: 'Are you using HPP Co-Branding?',
                                            buttons: [
                                                { label: 'Yes', value: 'yes' },
                                                { label: 'No', value: 'no' }
                                            ],
                                            disabled: isAgreement1TrueFromState || isAgreement2TrueFromState
                                        }}
                                    />
                                </Form.Field>

                            </Form.Group>

                            <Header as='h5'>Social Media Platforms</Header>
                            <FieldArray name="socialMediaPlatforms" component={renderPlatformFields} disabled={isAgreement1TrueFromState || isAgreement2TrueFromState} />

                        </Segment>

                        {/* Agreement */}
                        <Segment>
                            <Header as='h4'>
                                <Icon name='warning circle' color='yellow' /> Agreement
                            </Header>
                            <Form.Group>
                                <div style={{ marginBottom: '10px' }}> {/* Optional: Add spacing */}
                                    <Form.Field>
                                        <Field
                                            name='agreement1' // Unique name for the first checkbox
                                            component={renderCheckbox}
                                            otherProps={{
                                                label: `I hereby confirm that the information provided above is accurate and complete. I also consent to use ${email} as my primary email address for all communications related to Hikvision services.`,
                                                required: true,
                                                disabled: !!anyValidationError
                                            }}
                                        />
                                    </Form.Field>
                                </div>
                            </Form.Group>
                            <Form.Group>
                                <div style={{ marginBottom: '10px' }}> {/* Optional: Add spacing */}
                                    <Form.Field>
                                        <Field
                                            name='agreement2' // Unique name for the second checkbox
                                            component={renderCheckbox}
                                            otherProps={{
                                                label: `I acknowledge that I have read and agree to Hikvision Sri Lanka's Privacy Policy and General Terms of Use.`,
                                                required: true,
                                                disabled: !!anyValidationError
                                            }}
                                        />
                                    </Form.Field>
                                </div>
                            </Form.Group>
                            <div style={{ display: 'flex', gap: '10px' }}> {/* Flex display for horizontal alignment and gap */}
                                <InfoModal type="privacyPolicy" />
                                <InfoModal type="generalTermOfUse" />
                            </div>
                        </Segment>

                    </Form>
                </Segment >
            </Modal.Content >
            {/* Submit Buttons */}
            < Modal.Actions >
                <Button onClick={handleModalClose}>
                    <Icon name='remove' /> Cancel
                </Button>
                <Button color='black' onClick={handleSubmit(handlePartnerIDData)} disabled={!isAgreement1TrueFromState || !isAgreement2TrueFromState || !selectedProfileImage}  >
                    <Icon name='checkmark' /> Ok
                </Button>
            </Modal.Actions >



        </>
    );

};

// Form validation function
const validate = (values) => {
    const errors = {};

    const {
        hikID,
        nic,
        city,
        province,
        fullAddress,
        birthday,
        haveExperienceInCCTV,
        haveExperienceInNonCCTV,
        cctvStartYear,
        nonCCTVStartYear,
        roleInCompany,
        numberOfSitesCompletedInCompany,
        usedHikvisionProducts,
        hppCoBrandingStatus,
        socialMediaPlatforms
    } = values;


    // Validate hikID for the specific pattern
    const hikIDPattern = /^hik_\d+$/; // Regular expression for 'hik_' followed by digits
    if (!hikID) {
        errors.hikID = "Required";
    } else if (!hikIDPattern.test(hikID)) {
        errors.hikID = "Must start with 'hik_' followed by numeric characters.";
    }

    if (!nic) errors.nic = "Required";
    if (!city) errors.city = "Required";
    if (!province) errors.province = "Required";
    if (!fullAddress) errors.fullAddress = "Required";
    if (!birthday) errors.birthday = "Required";

    if (!haveExperienceInCCTV) {
        errors.haveExperienceInCCTV = "Required";
    } else if (haveExperienceInCCTV === 'yes' && !cctvStartYear) {
        errors.cctvStartYear = "Required";
    }

    if (!haveExperienceInNonCCTV) {
        errors.haveExperienceInNonCCTV = "Required";
    } else if (haveExperienceInNonCCTV === 'yes' && !nonCCTVStartYear) {
        errors.nonCCTVStartYear = "Required";
    }



    if (!roleInCompany) errors.roleInCompany = "Required";
    if (!numberOfSitesCompletedInCompany) errors.numberOfSitesCompletedInCompany = "Required";

    // Validate usedHikvisionProducts for at least one selection
    if (!usedHikvisionProducts || usedHikvisionProducts.length === 0) {
        errors.usedHikvisionProducts = "At least one option must be selected.";
    }
    if (!hppCoBrandingStatus) errors.hppCoBrandingStatus = "Required";

    // Only validate if socialMediaPlatforms exist
    if (socialMediaPlatforms && socialMediaPlatforms.length > 0) {
        const platformErrors = [];

        socialMediaPlatforms.forEach((platform, index) => {
            const platformError = {};
            // Check if name is provided
            if (!platform.name) {
                platformError.name = "Platform name is required";
            }
            // Check if link is provided
            if (!platform.link) {
                platformError.link = "Platform link is required";
            }
            // If there are errors, add them to the platformErrors array
            if (Object.keys(platformError).length > 0) {
                platformErrors[index] = platformError;
            }
        });

        // Only assign platformErrors if there are any
        if (platformErrors.length > 0) {
            errors.socialMediaPlatforms = platformErrors;
        }
    }

    return errors;
};

export default reduxForm({
    form: "partnerID",
    validate
})(PartnerID);
